import { addDays, daysInMonth, diffDays, formatLocalDate, isoWeek, monthIndex, parseLocalDate, startOfWeek, weekdayIndex, weekdayOf, weekdayOfYmd, } from './localDate.js';
import { validateCount, validateRule } from './rrule.js';
import { MAX_CANDIDATES_PER_EXPANSION, MAX_OCCURRENCES_PER_EXPANSION, RecurrenceError, } from './types.js';
// De generator (§5.2): itereert over LOKALE KALENDERDATUMS en kent geen tijdzones. Volgorde van
// toepassing, per kandidaat-datum oplopend:
//   1. regel-kandidaten per periode (dag/week/maand/jaar, INTERVAL vanaf DTSTART);
//   2. kandidaten vóór DTSTART vallen af; DTSTART telt alleen als hij aan de regel voldoet;
//   3. UNTIL (inclusief, als wandkloktijd van het slot) stopt de reeks;
//   4. NL-filters week_parity (ISO-week) en excludeRanges (pauze/vakantie) → slot telt NIET;
//   5. COUNT telt de overgebleven (gerenderde) slots;
//   6. EXDATE haalt slots weg zonder COUNT te verlengen; RDATE voegt datums toe.
// Afwijking van RFC 5545, bewust (§5.2): jaarlijks op 29 februari valt in niet-schrikkeljaren op
// 28 februari (de ICS-export voegt daar een RDATE voor toe). BYMONTHDAY=31 in een maand van 30
// dagen vervalt wél conform RFC.
/** Normaliseert `HH:MM[:SS]` naar `HH:MM:SS`. */
function normTime(time) {
    if (!time)
        return '00:00:00';
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec(time);
    if (!m)
        throw new RecurrenceError('invalid_value', `ongeldige starttijd ${time}`);
    return `${m[1]}:${m[2]}:${m[3] ?? '00'}`;
}
/** Normaliseert UNTIL naar `YYYY-MM-DD` (hele dag) of `YYYY-MM-DDTHH:MM:SS`. */
function normUntil(until) {
    const v = until.trim().replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(v))
        return v;
    const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(v);
    if (!m)
        throw new RecurrenceError('invalid_value', `ongeldige until ${until}`);
    return `${m[1]}T${m[2]}:${m[3]}:${m[4] ?? '00'}`;
}
function validatePattern(p) {
    parseLocalDate(p.startDate);
    if (p.rule)
        validateRule(p.rule);
    if (p.count !== undefined && p.count !== null) {
        if (p.until !== undefined && p.until !== null) {
            throw new RecurrenceError('count_and_until', 'count en until sluiten elkaar uit');
        }
        validateCount(p.count);
    }
}
/** Datums in één maand volgens BYMONTHDAY/BYDAY (of de dag van DTSTART), oplopend. */
function monthDates(year, month, rule, dtDay) {
    const dim = daysInMonth(year, month);
    let fromMonthDay = null;
    if (rule.byMonthDay && rule.byMonthDay.length > 0) {
        fromMonthDay = rule.byMonthDay
            .map((v) => (v > 0 ? v : dim + 1 + v))
            .filter((d) => d >= 1 && d <= dim);
    }
    let fromByDay = null;
    if (rule.byDay && rule.byDay.length > 0) {
        fromByDay = [];
        for (const bd of rule.byDay)
            fromByDay.push(...weekdayDatesInMonth(year, month, bd, dim));
    }
    let days;
    if (fromMonthDay && fromByDay) {
        const set = new Set(fromByDay);
        days = fromMonthDay.filter((d) => set.has(d));
    }
    else if (fromMonthDay) {
        days = fromMonthDay;
    }
    else if (fromByDay) {
        days = fromByDay;
    }
    else {
        days = dtDay <= dim ? [dtDay] : [];
    }
    return [...new Set(days)].sort((a, b) => a - b);
}
function weekdayDatesInMonth(year, month, bd, dim) {
    const firstDow = weekdayIndex(weekdayOfYmd({ year, month, day: 1 }));
    const first = 1 + ((weekdayIndex(bd.weekday) - firstDow + 7) % 7);
    const all = [];
    for (let d = first; d <= dim; d += 7)
        all.push(d);
    if (bd.ordinal === undefined)
        return all;
    const idx = bd.ordinal > 0 ? bd.ordinal - 1 : all.length + bd.ordinal;
    const hit = all[idx];
    return hit === undefined ? [] : [hit];
}
/**
 * Lazy iterator over de regel-kandidaten vanaf DTSTART (stap 1–2). `skipTo` mag alleen gezet
 * worden als COUNT niet telt: dan springt de iterator direct naar de periode van die datum.
 */
// Een regel die nooit een datum oplevert (BYMONTHDAY=30 in februari) mag niet eindeloos lopen:
// na MAX_PERIODS perioden of voorbij jaar 9999 eindigt de reeks gewoon.
const MAX_PERIODS = 100_000;
const MAX_YEAR = 9999;
function* ruleCandidates(rule, start, skipTo) {
    const interval = rule.interval ?? 1;
    const s = parseLocalDate(start);
    switch (rule.freq) {
        case 'DAILY': {
            let k = 0;
            if (skipTo !== null) {
                const ahead = diffDays(start, skipTo);
                if (ahead > 0)
                    k = Math.floor(ahead / interval);
            }
            const allowed = rule.byDay ? new Set(rule.byDay.map((d) => d.weekday)) : null;
            for (let periods = 0; periods < MAX_PERIODS; periods += 1) {
                const date = addDays(start, k * interval);
                if (!allowed || allowed.has(weekdayOf(date)))
                    yield date;
                k += 1;
            }
            return;
        }
        case 'WEEKLY': {
            const ws = rule.weekStart ?? 'MO';
            const offsets = [
                ...new Set((rule.byDay && rule.byDay.length > 0
                    ? rule.byDay.map((d) => d.weekday)
                    : [weekdayOf(start)]).map((w) => (weekdayIndex(w) - weekdayIndex(ws) + 7) % 7)),
            ].sort((a, b) => a - b);
            const firstWeek = startOfWeek(start, ws);
            let w = 0;
            if (skipTo !== null) {
                const ahead = diffDays(firstWeek, startOfWeek(skipTo, ws));
                if (ahead > 0)
                    w = Math.floor(ahead / 7 / interval);
            }
            for (let periods = 0; periods < MAX_PERIODS; periods += 1) {
                const base = addDays(firstWeek, w * 7 * interval);
                for (const off of offsets)
                    yield addDays(base, off);
                w += 1;
            }
            return;
        }
        case 'MONTHLY': {
            const m0 = monthIndex(s);
            let k = 0;
            if (skipTo !== null) {
                const ahead = monthIndex(parseLocalDate(skipTo)) - m0;
                if (ahead > 0)
                    k = Math.floor(ahead / interval);
            }
            for (let periods = 0; periods < MAX_PERIODS; periods += 1) {
                const mi = m0 + k * interval;
                const year = Math.floor(mi / 12);
                if (year > MAX_YEAR)
                    return;
                const month = (mi % 12) + 1;
                for (const day of monthDates(year, month, rule, s.day)) {
                    yield formatLocalDate({ year, month, day });
                }
                k += 1;
            }
            return;
        }
        case 'YEARLY': {
            let k = 0;
            if (skipTo !== null) {
                const ahead = parseLocalDate(skipTo).year - s.year;
                if (ahead > 0)
                    k = Math.floor(ahead / interval);
            }
            const months = rule.byMonth && rule.byMonth.length > 0
                ? [...new Set(rule.byMonth)].sort((a, b) => a - b)
                : [s.month];
            const explicit = Boolean((rule.byMonthDay && rule.byMonthDay.length > 0) || (rule.byDay && rule.byDay.length > 0));
            for (;;) {
                const year = s.year + k * interval;
                if (year > MAX_YEAR)
                    return;
                for (const month of months) {
                    if (explicit) {
                        for (const day of monthDates(year, month, rule, s.day)) {
                            yield formatLocalDate({ year, month, day });
                        }
                    }
                    else {
                        const ymd = yearlyDay(year, month, s);
                        if (ymd)
                            yield formatLocalDate(ymd);
                    }
                }
                k += 1;
            }
        }
    }
}
/** Jaarlijks op de dag van DTSTART; 29 februari → 28 februari in niet-schrikkeljaren (§5.2). */
function yearlyDay(year, month, s) {
    const dim = daysInMonth(year, month);
    if (s.day <= dim)
        return { year, month, day: s.day };
    if (month === 2 && s.day === 29)
        return { year, month, day: 28 };
    return null;
}
function passesFilters(date, p) {
    if (p.weekParity) {
        const even = isoWeek(date).week % 2 === 0;
        if ((p.weekParity === 'even') !== even)
            return false;
    }
    if (p.excludeRanges) {
        for (const r of p.excludeRanges)
            if (date >= r.from && date <= r.to)
                return false;
    }
    return true;
}
/**
 * Iterator over de regel-slots van een serie (stap 1–5), oplopend en onbegrensd tenzij
 * UNTIL/COUNT de reeks sluit. EXDATE/RDATE zitten hier NIET in (zie generateDates).
 */
export function* iterateRuleSlots(p, skipTo = null) {
    validatePattern(p);
    if (!p.rule) {
        const until = p.until ? normUntil(p.until) : null;
        if (!until || withinUntil(p.startDate, normTime(p.startTime), until))
            yield p.startDate;
        return;
    }
    const time = normTime(p.startTime);
    const until = p.until ? normUntil(p.until) : null;
    const counting = p.count !== undefined && p.count !== null;
    let produced = 0;
    let seen = 0;
    for (const date of ruleCandidates(p.rule, p.startDate, counting ? null : skipTo)) {
        seen += 1;
        if (seen > MAX_CANDIDATES_PER_EXPANSION) {
            throw new RecurrenceError('too_many', 'te veel kandidaten; venster of regel te groot');
        }
        if (date < p.startDate)
            continue;
        if (until && !withinUntil(date, time, until))
            return;
        if (!passesFilters(date, p))
            continue;
        produced += 1;
        if (counting && produced > p.count)
            return;
        yield date;
    }
}
function withinUntil(date, time, until) {
    if (until.length === 10)
        return date <= until;
    return `${date}T${time}` <= until;
}
/**
 * Regel-slots met datum in `window` (stap 1–5), zonder EXDATE/RDATE. Voor COUNT-splits en de
 * bestaanscontrole van override-sleutels ("is dit nog een slot van de regel?").
 */
export function ruleSlotsInWindow(p, window) {
    if (window.to < window.from)
        return [];
    const out = [];
    for (const date of iterateRuleSlots(p, window.from)) {
        if (date > window.to)
            break;
        if (date >= window.from) {
            out.push(date);
            if (out.length > MAX_OCCURRENCES_PER_EXPANSION) {
                throw new RecurrenceError('too_many', 'te veel occurrences in het venster');
            }
        }
    }
    return out;
}
/**
 * Alle datums van de serie in `window` (inclusief), oplopend en uniek: regel-slots minus
 * EXDATE plus RDATE. Eenmalig (rule = null): alleen DTSTART.
 */
export function generateDates(p, window) {
    parseLocalDate(window.from);
    parseLocalDate(window.to);
    const ex = new Set(p.exdates ?? []);
    const set = new Set(ruleSlotsInWindow(p, window).filter((d) => !ex.has(d)));
    for (const r of p.rdates ?? []) {
        if (r >= window.from && r <= window.to && !ex.has(r))
            set.add(r);
    }
    return [...set].sort();
}
/** Is `date` een slot van de serie (regel-slot of RDATE, niet ge-EXDATE'd)? */
export function isSlot(p, date) {
    return generateDates(p, { from: date, to: date }).length === 1;
}
/** Aantal regel-slots strikt vóór `date` (COUNT-verdeling bij een split: k). */
export function countRuleSlotsBefore(p, date) {
    if (date <= p.startDate)
        return 0;
    let n = 0;
    for (const d of iterateRuleSlots(p)) {
        if (d >= date)
            break;
        n += 1;
    }
    return n;
}
/** Totaal aantal regel-slots (alleen eindige series: COUNT of UNTIL); anders null. */
export function totalRuleSlots(p) {
    if (p.rule && (p.count === undefined || p.count === null) && !p.until)
        return null;
    let n = 0;
    for (const d of iterateRuleSlots(p)) {
        void d;
        n += 1;
    }
    return n;
}
/**
 * Eerste slot op of na `from` binnen `horizonDays`, of null. Gebruikt om DTSTART te
 * normaliseren (RFC 5545: DTSTART hoort bij de regel te passen).
 */
export function firstSlotOnOrAfter(p, from, horizonDays = 3 * 366) {
    const to = addDays(from, horizonDays);
    for (const date of iterateRuleSlots(p, from)) {
        if (date > to)
            return null;
        if (date >= from)
            return date;
    }
    return null;
}
/** Laatste regel-slot (eindige series); null bij oneindig of leeg. */
export function lastRuleSlot(p) {
    if (p.rule && (p.count === undefined || p.count === null) && !p.until)
        return null;
    let last = null;
    for (const d of iterateRuleSlots(p))
        last = d;
    return last;
}
//# sourceMappingURL=generate.js.map